import type { ReactNode } from "react"

import type { CsvColumnDef as ColumnDef } from "@/utils/csv/tableCsvColumns"
import {
  formatFullTimestamp,
  formatRelativeTimestamp,
} from "@/utils/formatting/format-timestamp"

import {
  notebookScheduleLabel,
  type NotebookRow,
} from "../utils/notebookSeries"

const DATE_COLUMN_WIDTH = 160
const CREATOR_COLUMN_WIDTH = 200

export function buildNotebookColumns({
  renderNameEditor,
  resolveCreator,
}: {
  /** The open rename field for a notebook, or null to show its title. */
  renderNameEditor: (notebook: NotebookRow) => ReactNode
  resolveCreator: (creator: string | null | undefined) => string
}): ColumnDef<NotebookRow>[] {
  return [
    {
      id: "title",
      csvValue: (row) => row.title,
      header: "Name",
      accessorKey: "title",
      width: 420,
      cell: (notebook) =>
        renderNameEditor(notebook) ?? (
          <span className="min-w-0 truncate font-medium text-coval-text">
            {notebook.title}
          </span>
        ),
    },
    {
      id: "schedule",
      header: "Schedule",
      width: 210,
      csvValue: notebookScheduleLabel,
      cell: (notebook) => (
        <div className="text-coval-text-muted">
          <div>{notebookScheduleLabel(notebook)}</div>
          {notebook.kind === "series" && (
            <div className="text-xs">
              {notebook.editions?.length ?? 0}{" "}
              {notebook.historyPartial ? "available " : ""}
              {notebook.editions?.length === 1 ? "edition" : "editions"}
              {notebook.schedule?.last_run?.status === "failed"
                ? " · Last run failed"
                : ""}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "created_at",
      csvValue: (row) => row.created_at,
      header: "Latest notebook",
      width: DATE_COLUMN_WIDTH,
      cell: (notebook) => (
        <span
          className="text-coval-text-muted"
          title={formatFullTimestamp(notebook.created_at)}
        >
          {notebook.kind === "series" &&
          !notebook.editions?.length &&
          !notebook.template
            ? "—"
            : formatRelativeTimestamp(notebook.created_at)}
        </span>
      ),
    },
    {
      id: "created_by",
      csvValue: (row) => resolveCreator(row.created_by),
      header: "Created by",
      width: CREATOR_COLUMN_WIDTH,
      cell: (notebook) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {resolveCreator(notebook.created_by)}
        </span>
      ),
    },
  ]
}
